import { useAppStore } from '@/store/store';
import { selectCargoDensity, selectBreakEvenDensity, selectFreightClass, selectShipmentSummary } from '@/store/selectors';
import { Tooltip } from '@/components/shared/Tooltip';
import { EmptyState } from '@/components/shared/EmptyState';
import { Gauge } from 'lucide-react';

export function DensityPanel() {
  const density = useAppStore(selectCargoDensity);
  const breakEven = useAppStore(selectBreakEvenDensity);
  const freightClass = useAppStore(selectFreightClass);
  const summary = useAppStore(selectShipmentSummary);
  const isEmpty = summary.totalCBM === 0;

  const pct = breakEven > 0 ? Math.min((density / (breakEven * 2)) * 100, 100) : 0;
  const getColor = (): string => {
    if (density <= 0) return 'var(--color-border)';
    if (density < breakEven) return 'var(--color-accent)';
    return 'var(--color-warning)';
  };

  return (
    <div className="card" data-testid="density-panel">
      <h3 className="section-title">Cargo Density & Freight Class</h3>
      {isEmpty ? (
        <EmptyState
          icon={Gauge}
          compact
          title="Density analysis appears here"
          description="Add packages on the Calculator tab to see cargo density, break-even density, and freight class."
        />
      ) : (
        <div className="metric-grid">
          <div className="metric-box">
            <Tooltip content="Total shipment weight divided by total volume (kg/m³). Determines whether billing is weight- or volume-driven.">
              <div className="metric-label" style={{ cursor: 'help' }}>Density</div>
            </Tooltip>
            <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-bold)' }}>{density.toFixed(2)} kg/m³</div>
          </div>
          <div className="metric-box">
            <Tooltip content="Density threshold where actual weight equals dimensional weight. Below this, charges are volume-driven.">
              <div className="metric-label" style={{ cursor: 'help' }}>Break-Even Density</div>
            </Tooltip>
            <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-bold)' }}>{breakEven.toFixed(2)} kg/m³</div>
          </div>
          <div className="metric-box">
            <Tooltip content="Cargo classification based on density: light cargo is billed by volume, heavy cargo by weight.">
              <div className="metric-label" style={{ cursor: 'help' }}>Freight Class</div>
            </Tooltip>
            <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-bold)' }}>{freightClass}</div>
          </div>
        </div>
      )}
      {!isEmpty && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <div className="progress-track" style={{ height: 12, borderRadius: 6 }}>
            <div
              className="progress-fill"
              style={{ width: `${pct}%`, background: getColor(), borderRadius: 6 }}
            />
            {breakEven > 0 && (
              <div style={{ position: 'absolute', left: `${Math.min((breakEven / (breakEven * 2)) * 100, 100)}%`, top: -2, width: 2, height: 16, background: 'var(--color-error)' }} title="Break-even" />
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--space-1)' }}>
            <span>0 kg/m³</span>
            <span>Break-even: {breakEven.toFixed(0)}</span>
            <span>{(breakEven * 2).toFixed(0)} kg/m³</span>
          </div>
        </div>
      )}
    </div>
  );
}
